// ─── Theme definitions ────────────────────────────────────────────────────────

import type { Status } from './data';
import { iconSvg } from './icons';

export type ThemeMode = 'night' | 'day';

export interface Theme {
  mode:       ThemeMode;
  background: number;

  // Connection lines
  lineColor:  number;
  lineAlpha:  number;
  lineWidth:  number;

  // Node colors by level
  nodeColors: number[];

  // Text
  textColor:  number;

  // Status badge
  statusColors: Record<Status, number>;
  statusBorderColor: number;

  // Action bar
  deleteColor: number;
  rotateColor: number;
  addColor:    number;
  actionIconColor: number;

  // Pulse ring shown on the focused node when picked from the panel
  pulseColor: number;

  // CSS vars (for HTML overlay)
  css: {
    bg:          string;
    text:        string;
    btnBg:       string;
    btnText:     string;
    btnBorder:   string;
    modalBg:     string;
    modalBorder: string;
    modalShadow: string;
    inputBg:     string;
  };
}

// ─── Night — deep space, neon accents, sharp geometry ────────────────────────

export const nightTheme: Theme = {
  mode:       'night',
  background: 0x0a0620,

  lineColor:  0x452e5a,
  lineAlpha:  1,
  lineWidth:  1,

  // Palette: coral, gold, muted purple, deep purple
  nodeColors: [0xff653f, 0xffc85c, 0x452e5a, 0x1e104e],

  textColor: 0xffffff,

  statusColors: {
    'todo':        0x9ca3af,
    'in-progress': 0x3b82f6,
    'done':        0x22c55e,
    'blocked':     0xef4444,
  },
  statusBorderColor: 0x0a0620,

  deleteColor:     0xef4444,
  rotateColor:     0x3b82f6,
  addColor:        0x10b981,
  actionIconColor: 0xffffff,

  pulseColor: 0xffc85c,

  css: {
    bg:          '#0a0620',
    text:        '#ffc85c',
    btnBg:       'rgba(10,6,32,0.9)',
    btnText:     '#ffc85c',
    btnBorder:   '#452e5a',
    modalBg:     '#1e104e',
    modalBorder: '#452e5a',
    modalShadow: '0 20px 60px rgba(0,0,0,0.7)',
    inputBg:     '#100828',
  },
};

// ─── Day — warm white, ink borders, vivid fills ───────────────────────────────

export const dayTheme: Theme = {
  mode:       'day',
  background: 0xfafafa,

  lineColor:  0xd1d5db,
  lineAlpha:  1,
  lineWidth:  1.5,

  // Pastel palette: sky-blue, thistle, baby-pink, icy-blue
  nodeColors: [0xa2d2ff, 0xcdb4db, 0xffafcc, 0xbde0fe],

  textColor: 0x1f2433,

  statusColors: {
    'todo':        0x9ca3af,
    'in-progress': 0x2563eb,
    'done':        0x16a34a,
    'blocked':     0xdc2626,
  },
  statusBorderColor: 0xfafafa,

  deleteColor:     0xdc2626,
  rotateColor:     0x2563eb,
  addColor:        0x059669,
  actionIconColor: 0xffffff,

  pulseColor: 0x4f46e5,

  css: {
    bg:          '#fafafa',
    text:        '#4f46e5',
    btnBg:       'rgba(250,250,250,0.92)',
    btnText:     '#4f46e5',
    btnBorder:   '#e5e7eb',
    modalBg:     '#ffffff',
    modalBorder: '#e5e7eb',
    modalShadow: '0 20px 60px rgba(0,0,0,0.18)',
    inputBg:     '#f5f5f5',
  },
};

// ─── State ────────────────────────────────────────────────────────────────────

// Navigation between routes uses full page loads, so the active theme is kept in
// localStorage — otherwise every navigation would reset to night.
const STORAGE_KEY = 'dictator-theme';

function loadInitialTheme(): Theme {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'day' ? dayTheme : nightTheme;
  } catch {
    return nightTheme;
  }
}

let _current: Theme = loadInitialTheme();

export function currentTheme(): Theme {
  return _current;
}

export function toggleTheme(): Theme {
  _current = _current.mode === 'night' ? dayTheme : nightTheme;
  try {
    window.localStorage.setItem(STORAGE_KEY, _current.mode);
  } catch {
    /* storage unavailable (e.g. private mode) — theme just won't persist */
  }
  return _current;
}

// ─── CSS overlay theming ───────────────────────────────────────────────────────

const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/** Push the current theme into the CSS custom properties every HTML overlay reads. */
export function applyCssTheme(): void {
  const t = _current;
  const root = document.documentElement.style;
  root.setProperty('--bg',                  t.css.bg);
  root.setProperty('--text',                t.css.text);
  root.setProperty('--btn-bg',              t.css.btnBg);
  root.setProperty('--btn-text',            t.css.btnText);
  root.setProperty('--btn-border',          t.css.btnBorder);
  root.setProperty('--modal-bg',            t.css.modalBg);
  root.setProperty('--modal-border',        t.css.modalBorder);
  root.setProperty('--modal-shadow',        t.css.modalShadow);
  root.setProperty('--input-bg',            t.css.inputBg);
  root.setProperty('--status-todo',         hex(t.statusColors['todo']));
  root.setProperty('--status-in-progress',  hex(t.statusColors['in-progress']));
  root.setProperty('--status-done',         hex(t.statusColors['done']));
  root.setProperty('--status-blocked',      hex(t.statusColors['blocked']));
}

/**
 * Wire a plain DOM button as a theme toggle — used by the pages list and API
 * docs views, which have no PixiJS canvas. (The map view has its own toggle
 * because it must also repaint the canvas and node sprites.)
 */
export function setupDomThemeToggle(btn: HTMLButtonElement): void {
  const paint = (): void => {
    const mode = _current.mode;
    // Night mode offers a switch to day (sun); day mode a switch to night (moon).
    btn.innerHTML = iconSvg(mode === 'night' ? 'sun' : 'moon', 16);
    btn.setAttribute('aria-label', mode === 'night' ? 'Switch to day theme' : 'Switch to night theme');
  };
  paint();
  btn.addEventListener('click', () => {
    toggleTheme();
    applyCssTheme();
    paint();
  });
}
